import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashLoading from "../components/DashLoading";
import { useIstToday } from "../lib/istDate";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { userPages } from "../lib/pages";
import { DashHero, DashCaption, Section, Tile, TileGrid, DashTable, useDashImage, n, rupees } from "../components/TempleDash";
import { IndianRupee, Coins, Banknote, CalendarDays, CalendarRange, TrendingUp, Gem, Landmark, Globe2, FileStack, FilePlus2, FileBarChart, History } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const AX = { fontSize: 11, fill: "#64748B" };
const dmy = (d) => (d ? d.split("-").reverse().join("/") : "-");
const grams = (v) => {
  const g = Number(v || 0);
  return g >= 1000 ? `${(g / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 })} kg` : `${g.toLocaleString("en-IN", { maximumFractionDigits: 3 })} g`;
};
const short = (v) => {
  const x = Number(v || 0);
  return x >= 1e7 ? `₹${(x / 1e7).toFixed(2)} Cr` : x >= 1e5 ? `₹${(x / 1e5).toFixed(2)} L` : rupees(x);
};

export default function HundialDashboard() {
  const { user } = useAuth();
  const pages = userPages(user);
  const isSA = user.role === "SUPER_ADMIN";
  const [templeId, setTempleId] = useState("");
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const { url, temple, temples } = useDashImage("HUNDIAL", templeId, user);

  const load = useCallback(() => {
    api.get("/hundial/dashboard", { params: templeId ? { temple_id: templeId } : {} })
      .then((r) => { setD(r.data); setError(null); }).catch((e) => setError(e));
  }, [templeId]);
  const istDay = useIstToday(); // new day at 00:00 IST: reload today's figures at once
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load, istDay]);

  const last = d?.last;
  const y = d?.year_stats || {};
  const tname = (tid) => temples.find((t) => t.temple_id === tid)?.name || tid;

  return (
    <div className="space-y-4" data-testid="hundial-dashboard">
      <DashHero image={url} testid="hundial-hero"
        left={d ? [
          { icon: IndianRupee, label: "Last Opening Total", value: last ? short(last.cash_total) : "--", testid: "hd-last-total" },
          { icon: TrendingUp, label: "Average One Day", value: last ? rupees(last.avg_per_day) : "--", testid: "hd-last-avg" },
          { icon: CalendarDays, label: "Last Opened On", value: last ? dmy(last.opened_date) : "--", testid: "hd-last-date" },
          { icon: CalendarRange, label: "Period", value: last ? `${last.period_days} days` : "--", testid: "hd-last-days" },
        ] : []}
        right={d ? [
          { icon: Landmark, label: `Collection ${d.year}`, value: short(y.cash), testid: "hd-year-cash" },
          { icon: FileStack, label: `Openings ${d.year}`, value: n(y.reports), sub: d.reports_total !== y.reports ? n(d.reports_total) : "", testid: "hd-year-reports" },
          { icon: Gem, label: `Gold ${d.year}`, value: grams(y.gold_g), testid: "hd-year-gold" },
          { icon: Gem, label: `Silver ${d.year}`, value: grams(y.silver_g), testid: "hd-year-silver" },
        ] : []} />

      <DashCaption temple={temple} title="Hundial Dashboard">
        {isSA && (
          <select value={templeId} onChange={(e) => setTempleId(e.target.value)} className="h-10 rounded-lg border border-[#E5D9C5] px-3 text-sm bg-white" data-testid="hd-temple">
            <option value="">All temples</option>
            {temples.map((x) => <option key={x.temple_id} value={x.temple_id}>{x.temple_id} · {x.name}</option>)}
          </select>
        )}
        {pages.includes("HUNDIAL_ENTRY") && <Link to="/hundial/entry" className="h-10 px-4 rounded-lg bg-[#B0121F] text-white text-sm font-semibold inline-flex items-center gap-1.5"><FilePlus2 className="w-4 h-4" /> New Opening</Link>}
        {pages.includes("HUNDIAL_REPORTS") && <Link to="/hundial/reports" className="h-10 px-4 rounded-lg border border-[#E5D9C5] bg-white text-maroon-800 text-sm font-semibold inline-flex items-center gap-1.5"><FileBarChart className="w-4 h-4" /> Reports</Link>}
      </DashCaption>

      {!d ? <DashLoading error={error} onRetry={() => { setError(null); load(); }} testid="hd-loading" /> : (
        <>
          {!last && (
            <div className="rounded-xl border border-dashed border-[#E8551E] bg-[#FFF7F0] p-6 text-center text-slate-600" data-testid="hd-empty">
              No Hundial openings recorded yet{pages.includes("HUNDIAL_ENTRY") ? " - use New Opening to enter the first one." : "."}
            </div>
          )}

          {last && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Section title={`Last Opening · ${dmy(last.opened_date)}`}>
                <TileGrid cols={3}>
                  <Tile icon={Banknote} label="Notes" value={short(last.cash_notes)} tone="blue" />
                  <Tile icon={Coins} label="Coins" value={short(last.cash_coins)} tone="green" />
                  <Tile icon={IndianRupee} label="Total" value={short(last.cash_total)} tone="yellow" />
                </TileGrid>
              </Section>
              <Section title="Gold & Silver (Last Opening)">
                <TileGrid cols={3}>
                  <Tile icon={Gem} label="Gold" value={grams(last.gold?.total_g)} tone="yellow" />
                  <Tile icon={Gem} label="Silver" value={grams(last.silver?.total_g)} tone="slate" />
                  <Tile icon={History} label="Old Notes" value={short(last.old_notes_total)} tone="red" />
                </TileGrid>
              </Section>
              <Section title={`This Year (${d.year})`}>
                <TileGrid cols={3}>
                  <Tile icon={FileStack} label="Openings" value={n(y.reports)} tone="purple" />
                  <Tile icon={CalendarRange} label="Days" value={n(y.days)} tone="blue" />
                  <Tile icon={TrendingUp} label="Avg / Day" value={y.days ? rupees(y.cash / y.days) : "--"} tone="green" />
                </TileGrid>
              </Section>
            </div>
          )}

          {d.trend.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Section title="Collection per Opening" className="lg:col-span-2" bodyClass="p-3 h-72">
                <ResponsiveContainer>
                  <BarChart data={d.trend.map((r) => ({ ...r, label: dmy(r.date).slice(0, 5) + (isSA && !templeId ? ` ${r.temple_id}` : "") }))} margin={{ top: 8, right: 8, left: 6, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0E6D2" vertical={false} />
                    <XAxis dataKey="label" tick={AX} tickLine={false} />
                    <YAxis tick={AX} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} />
                    <Tooltip cursor={{ fill: "#FDF2E9" }} formatter={(v) => rupees(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="cash" name="Total Rupees" fill="#D7261E" radius={[4, 4, 0, 0]} maxBarSize={38} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Section>
              <Section title={`Foreign Currency (${d.year})`} bodyClass="p-3 max-h-72 overflow-y-auto">
                <DashTable head={["Country (Currency)", "Quantity"]} empty="No foreign currency this year" testid="hd-foreign"
                  rows={d.foreign.map((f) => [<span key="c" className="inline-flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5 text-[#7C3AED]" />{f.name}</span>, n(f.amount)])} />
              </Section>
            </div>
          )}

          {isSA && !templeId && d.temple_wise.length > 0 && (
            <Section title={`Temple-wise Collection (${d.year})`}>
              <DashTable testid="hd-temple-wise" head={["Temple", "Openings", "Total Rupees"]}
                rows={d.temple_wise.map((t) => [tname(t.temple_id), n(t.reports), rupees(t.cash)])}
                foot={d.temple_wise.length > 1 ? ["TOTAL", n(y.reports), rupees(y.cash)] : null} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}
